import { useState } from 'react'
import { CalendarIcon } from '@radix-ui/react-icons'
import { DayPicker } from 'react-day-picker'
import 'react-day-picker/style.css'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/chadcn/Dialog'
import { Input } from '@/components/chadcn/Input'
import { Button } from '@/components/chadcn/Button'
import cn from '@/util/cn'

type DatePickerProps = {
  id?: string
  // ISO date string (YYYY-MM-DD), or "" / undefined when unset.
  value: string | undefined
  onChange: (value: string) => void
  disabled?: boolean
  isError?: boolean
}

const pad2 = (n: number): string => String(n).padStart(2, '0')

const formatMmDdYyyy = (iso: string | undefined): string => {
  if (!iso) return ''
  const [y, m, d] = iso.split('-')
  if (!y || !m || !d) return ''
  return `${m}/${d}/${y}`
}

const dateToIso = (date: Date): string =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`

const isoToDate = (iso: string | undefined): Date | undefined => {
  if (!iso) return undefined
  const [y, m, d] = iso.split('-').map(Number)
  if (!y || !m || !d) return undefined
  return new Date(y, m - 1, d)
}

const DatePicker = ({
  id,
  value,
  onChange,
  disabled,
  isError,
}: DatePickerProps) => {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<Date | undefined>(isoToDate(value))

  const handleOpen = () => {
    if (disabled) return
    setDraft(isoToDate(value))
    setOpen(true)
  }

  const handleApply = () => {
    if (draft) onChange(dateToIso(draft))
    setOpen(false)
  }

  return (
    <>
      <div className="relative">
        <Input
          id={id}
          type="text"
          readOnly
          value={formatMmDdYyyy(value)}
          placeholder="MM/DD/YYYY"
          onClick={handleOpen}
          disabled={disabled}
          className={cn(
            'cursor-pointer pr-10',
            isError ? 'ring-red-600 focus-visible:ring-red-600' : '',
          )}
        />
        <button
          type="button"
          onClick={handleOpen}
          disabled={disabled}
          aria-label="Open calendar"
          className="absolute inset-y-0 right-2 flex items-center text-primary-contrast/70"
        >
          <CalendarIcon className="h-5 w-5" />
        </button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[calc(100%-2rem)] max-w-sm [&>button.absolute]:hidden">
          <DialogHeader>
            <DialogTitle className="sr-only">Choose date</DialogTitle>
          </DialogHeader>
          <DayPicker
            mode="single"
            selected={draft}
            onSelect={setDraft}
            defaultMonth={draft}
            captionLayout="dropdown"
            endMonth={new Date(new Date().getFullYear() + 1, 11)}
            classNames={{
              selected: 'bg-primary text-white rounded-full',
              today: 'font-bold',
            }}
          />
          <div className="mt-2 flex justify-end gap-4">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setOpen(false)}
              className="text-sm"
            >
              Cancel
            </Button>
            <button
              type="button"
              onClick={handleApply}
              disabled={!draft}
              className="text-sm font-medium text-primary-contrast hover:underline disabled:text-primary-contrast/40"
            >
              Ok
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default DatePicker
